import React, { useState, useEffect } from 'react';
import portfolioService from '../../services/portfolio';

interface Props {
  portfolioId: number;
  onClose: () => void;
}

type Row = { key: string; value: string };

const humanize = (k: string) => k.replace(/_/g, ' ');

// View / adjust what Aumora remembers about the owner (household memory).
const MemoryPanel: React.FC<Props> = ({ portfolioId, onClose }) => {
  const [rows, setRows] = useState<Row[]>([]);
  const [originalKeys, setOriginalKeys] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');

  useEffect(() => {
    portfolioService
      .getHouseholdMemory(portfolioId)
      .then((mem) => {
        const r = Object.entries(mem).map(([key, value]) => ({ key, value: String(value) }));
        setRows(r);
        setOriginalKeys(r.map((x) => x.key));
      })
      .finally(() => setLoading(false));
  }, [portfolioId]);

  const setValue = (key: string, value: string) =>
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, value } : r)));
  const remove = (key: string) => setRows((prev) => prev.filter((r) => r.key !== key));

  const addRow = () => {
    const k = newKey.trim().toLowerCase().replace(/\s+/g, '_');
    if (!k) return;
    setRows((prev) => [...prev.filter((r) => r.key !== k), { key: k, value: newValue.trim() }]);
    setNewKey('');
    setNewValue('');
  };

  const save = async () => {
    setSaving(true);
    const facts: Record<string, string> = {};
    rows.forEach((r) => { facts[r.key] = r.value; });
    // Keys removed in the panel are forgotten (sent blank).
    const current = new Set(rows.map((r) => r.key));
    originalKeys.forEach((k) => { if (!current.has(k)) facts[k] = ''; });
    try {
      await portfolioService.updateHouseholdMemory(portfolioId, facts);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const input: React.CSSProperties = {
    fontSize: '0.82rem', borderColor: '#e4e4e7', padding: '5px 9px',
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,5,55,0.28)', zIndex: 1100,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 14, width: '100%', maxWidth: 520,
          maxHeight: '80vh', display: 'flex', flexDirection: 'column',
          boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
        }}
      >
        <div style={{ padding: '16px 18px 8px' }}>
          <div className="d-flex justify-content-between align-items-center">
            <span className="fw-semibold" style={{ fontSize: '0.95rem', color: '#000537' }}>
              What Aumora remembers
            </span>
            <button type="button" onClick={onClose} className="border-0 bg-transparent p-0"
              style={{ fontSize: '1.1rem', color: '#a1a1aa', cursor: 'pointer', lineHeight: 1 }}>×</button>
          </div>
          <div style={{ fontSize: '0.78rem', color: '#71717a', marginTop: 4 }}>
            Goals, risk tolerance, constraints, and preferences used to personalize answers.
          </div>
        </div>

        <div style={{ overflowY: 'auto', padding: '4px 18px', flex: 1 }}>
          {loading ? (
            <div style={{ fontSize: '0.85rem', color: '#a1a1aa', padding: '12px 0' }}>Loading…</div>
          ) : rows.length === 0 ? (
            <div style={{ fontSize: '0.85rem', color: '#a1a1aa', padding: '12px 0' }}>
              Nothing remembered yet. Tell Aumora about your goals or risk tolerance in the chat, or add a fact below.
            </div>
          ) : (
            rows.map((r) => (
              <div key={r.key} className="d-flex align-items-center gap-2" style={{ padding: '6px 0', borderTop: '1px solid #f4f4f5' }}>
                <span style={{ fontSize: '0.78rem', color: '#71717a', textTransform: 'capitalize', width: 130, flexShrink: 0 }}>
                  {humanize(r.key)}
                </span>
                <input
                  className="form-control"
                  style={input}
                  value={r.value}
                  onChange={(e) => setValue(r.key, e.target.value)}
                />
                <button type="button" onClick={() => remove(r.key)} className="border-0 bg-transparent p-0"
                  style={{ color: '#b91c1c', fontSize: '1rem', cursor: 'pointer', lineHeight: 1, flexShrink: 0 }}
                  title="Forget this">×</button>
              </div>
            ))
          )}

          {/* Add a fact */}
          <div className="d-flex align-items-center gap-2" style={{ padding: '10px 0 6px', marginTop: 6, borderTop: '1px solid #f4f4f5' }}>
            <input className="form-control" style={{ ...input, width: 130, flexShrink: 0 }} placeholder="New fact"
              value={newKey} onChange={(e) => setNewKey(e.target.value)} />
            <input className="form-control" style={input} placeholder="Value"
              value={newValue} onChange={(e) => setNewValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') addRow(); }} />
            <button type="button" onClick={addRow} className="border-0 bg-transparent p-0"
              style={{ color: '#000537', fontSize: '1.1rem', cursor: 'pointer', lineHeight: 1, flexShrink: 0 }}
              title="Add">＋</button>
          </div>
        </div>

        <div className="d-flex justify-content-end gap-2" style={{ padding: '12px 18px 16px' }}>
          <button type="button" onClick={onClose} className="btn"
            style={{ fontSize: '0.82rem', color: '#52525b', background: '#f4f4f5', padding: '6px 16px' }}>
            Cancel
          </button>
          <button type="button" onClick={save} disabled={saving} className="btn"
            style={{ fontSize: '0.82rem', background: '#000537', color: '#fff', padding: '6px 18px' }}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MemoryPanel;
